export const TEN_SEAT_MARKETING_URL = 'https://10seat.com';

const JSON_LD_SCRIPT_ID = 'microsite-restaurant-jsonld';

interface RestaurantAddress {
  street?: string;
  city?: string;
  postal_code?: string;
  state?: string;
  country?: string;
}

export interface RestaurantSeoInput {
  slug: string;
  name?: string;
  description?: string;
  image?: string;
  address?: RestaurantAddress;
  phone?: string;
  email?: string;
}

function upsertMeta(name: string, content: string, attribute: 'name' | 'property' = 'name'): void {
  if (typeof document === 'undefined') return;

  let element = document.head.querySelector(`meta[${attribute}="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function upsertCanonical(href: string): void {
  if (typeof document === 'undefined') return;

  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

function buildPageUrl(slug: string): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/restaurant/${slug}`;
  }
  return `https://bookatable.10seat.com/restaurant/${slug}`;
}

function buildAddress(address?: RestaurantAddress) {
  if (!address) return undefined;

  const streetAddress = (address.street || '').trim();
  const addressLocality = (address.city || '').trim();
  const postalCode = (address.postal_code || '').trim();
  const addressRegion = (address.state || '').trim();
  const addressCountry = (address.country || '').trim();

  if (!streetAddress && !addressLocality && !postalCode && !addressRegion && !addressCountry) {
    return undefined;
  }

  return {
    '@type': 'PostalAddress',
    ...(streetAddress ? { streetAddress } : {}),
    ...(addressLocality ? { addressLocality } : {}),
    ...(postalCode ? { postalCode } : {}),
    ...(addressRegion ? { addressRegion } : {}),
    ...(addressCountry ? { addressCountry } : {}),
  };
}

function buildRestaurantJsonLd(input: RestaurantSeoInput, pageUrl: string) {
  const restaurantName = (input.name || '').trim() || 'Restaurant';
  const description = (input.description || '').trim();
  const image = (input.image || '').trim();
  const phone = (input.phone || '').trim();
  const email = (input.email || '').trim();
  const postalAddress = buildAddress(input.address);

  return {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    name: restaurantName,
    url: pageUrl,
    acceptsReservations: true,
    ...(description ? { description } : {}),
    ...(image ? { image } : {}),
    ...(phone ? { telephone: phone } : {}),
    ...(email ? { email } : {}),
    ...(postalAddress ? { address: postalAddress } : {}),
    potentialAction: {
      '@type': 'ReserveAction',
      target: pageUrl,
    },
  };
}

export function applyRestaurantPageSeo(input: RestaurantSeoInput): void {
  if (typeof document === 'undefined') return;

  const pageUrl = buildPageUrl(input.slug);
  const restaurantName = (input.name || '').trim() || 'Restaurant';
  const title = `${restaurantName} – Reservations`;
  const description =
    (input.description || '').trim() ||
    `Book a table at ${restaurantName}. Reserve online in under a minute.`;

  document.title = title;
  upsertCanonical(pageUrl);
  upsertMeta('description', description);
  upsertMeta('og:title', title, 'property');
  upsertMeta('og:description', description, 'property');
  upsertMeta('og:url', pageUrl, 'property');
  upsertMeta('og:type', 'website', 'property');

  if (input.image) {
    upsertMeta('og:image', input.image, 'property');
  }

  const existingScript = document.getElementById(JSON_LD_SCRIPT_ID);
  existingScript?.remove();

  const script = document.createElement('script');
  script.id = JSON_LD_SCRIPT_ID;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(buildRestaurantJsonLd(input, pageUrl));
  document.head.appendChild(script);
}

export function clearRestaurantPageSeo(): void {
  if (typeof document === 'undefined') return;
  document.getElementById(JSON_LD_SCRIPT_ID)?.remove();
}
